/* eslint-disable react-hooks/exhaustive-deps */
import { RouterOutput } from "@mindlercare/mindlerapi-client";
import { useTheme } from "@mui/material";
import Dialog from "@mui/material/Dialog";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  useGetAllProgress,
  useGetIcbtClaims,
  useGetIcbtRequests,
  usePostIcbtRequests,
} from "api/useIcbtQueries";
import { useSnackbar } from "@mindlercare/ui-web";
import { marketFromCountryId } from "helpers/getCountryCode";
import { usePreviousValue } from "hooks";
import { AssignIcbtProvider, useAssignIcbt } from "./assignIcbtContext";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import * as R from "remeda";
import { styled } from "styled-components";
import { useIcbtLanguage } from "../hooks/useTreatment";
import { AssignIcbt } from "./assignIcbt";
import { mapTreatmentData } from "./common";
import { ConfirmAssignIcbtsModal } from "./confirmModal";
import { useGetAllProgramsAndTreatmentsQuery } from "../../../hooks/icbt/useGetAllProgramsAndTreatmentsQuery";

export const FixedContainer = styled.div`
  position: fixed;
  inset: 0;
  width: 90%;
  height: 90%;
  margin: auto;
  overflow-x: hidden;
`;

type GetPatientByUserIdResult = Exclude<
  RouterOutput["patients"]["getByUserId"],
  null
>;

type AssignIcbtModalProps = {
  patient: GetPatientByUserIdResult;
  closeModal: () => void;
  onAssignment?: () => void;
};

export const AssignIcbtModal = (props: AssignIcbtModalProps) => (
  <AssignIcbtProvider>
    <AssignIcbtModalContent {...props} />
  </AssignIcbtProvider>
);

const AssignIcbtModalContent = ({
  patient,
  closeModal,
  onAssignment,
}: AssignIcbtModalProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const { state: assignIcbtState, dispatch } = useAssignIcbt();
  const theme = useTheme();
  const smallerScreen = useMediaQuery(theme.breakpoints.down(1240), {
    noSsr: true,
  });
  const previousSmallerScreen = usePreviousValue(smallerScreen);

  const [, setSelectedProgramId] = useState<string | undefined>(undefined);
  const [selectedTreatmentId, setSelectedTreatmentId] = useState<
    string | undefined
  >(undefined);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const assignState = assignIcbtState.assignState;

  const language = useIcbtLanguage();
  // Data fetching
  const { data: programsAndTreatments } = useGetAllProgramsAndTreatmentsQuery(
    language?.codeISO2,
  );

  const { data: icbtRequests } = useGetIcbtRequests(patient.userId);

  const { data: allProgress } = useGetAllProgress(patient.userId?.toString());

  const { mutateAsync: postIcbts, isPending: isSubmitting } =
    usePostIcbtRequests();

  const { data: icbtClaims } = useGetIcbtClaims({
    userId: patient.userId,
    marketISO2: marketFromCountryId(patient.countryId),
  });

  const feedbackDateFeatureEnabledForPatient =
    icbtClaims?.hasIcbtResponseFeatureEnabled ?? false;

  // Check for errors, derive error status and log if there are errors

  // Derive data
  const treatments = useMemo(() => {
    return mapTreatmentData(
      programsAndTreatments,
      icbtRequests,
      allProgress,
      t,
    );
  }, [programsAndTreatments, allProgress, icbtRequests, t]);

  // derived states
  const { programsToAssign, treatmentOrder, programFeedbackDates } =
    assignState;

  const handlePreselectProgram = useCallback(
    (treatmentId: string) => {
      if (!programsAndTreatments) return;

      const preSelectedProgram = Object.values(
        programsAndTreatments.programs,
      ).find((program) => program.treatment.id === treatmentId);

      if (!preSelectedProgram) return;

      setSelectedProgramId(preSelectedProgram.id);
    },
    [setSelectedProgramId, programsAndTreatments, smallerScreen],
  );

  const closeAssignIcbtModal = useCallback(() => {
    closeModal();
  }, [closeModal]);

  // Using ref so this effect is only ran once - not every time screen size changes
  const introductionTreatmentInitialized = useRef(false);
  useEffect(() => {
    if (introductionTreatmentInitialized.current) return;
    if (!programsAndTreatments) return;

    const preSelectedTreatment = Object.values(
      programsAndTreatments.treatments,
    ).find((treatment) => treatment.introduction);

    if (!preSelectedTreatment) return;

    setSelectedTreatmentId(preSelectedTreatment.id);
    handlePreselectProgram(preSelectedTreatment.id);
    introductionTreatmentInitialized.current = true;
  }, [programsAndTreatments, smallerScreen, handlePreselectProgram]);

  useEffect(() => {
    // If changed from small screen to big screen
    if (previousSmallerScreen && !smallerScreen && selectedTreatmentId) {
      handlePreselectProgram(selectedTreatmentId);
    }
  }, [
    previousSmallerScreen,
    smallerScreen,
    handlePreselectProgram,
    selectedTreatmentId,
  ]);

  useEffect(() => {
    dispatch({ type: "resetAssignIcbtState" });
  }, []);

  const handleAssignIcbts = useCallback(async () => {
    const body = R.pipe(
      R.toPairs(programsToAssign),
      // use the order from user for treatments
      R.sortBy(([treatmentId]) => treatmentOrder.indexOf(treatmentId)),
      R.flatMap(([treatmentId, programIds]) => {
        // Sort programs by their natural order. Note: this means by locale specific
        // alphabetical order for stand-alone
        const { programs: programsInNaturalOrder } =
          treatments.byId[treatmentId];
        return R.pipe(
          programIds,
          R.sortBy((programId) =>
            programsInNaturalOrder.findIndex(
              (program) => program.id === programId,
            ),
          ),
          R.map((programId) => ({
            treatmentId,
            programId,
            userId: patient.userId,
            feedbackDate:
              feedbackDateFeatureEnabledForPatient && programFeedbackDates
                ? programFeedbackDates[programId]
                : undefined,
          })),
        );
      }),
    );

    try {
      await postIcbts({ body, userId: patient.userId });
      enqueueSnackbar({
        text: t("ASSIGN_ICBT_SUCCESS", {
          count: body.length,
        }),
      });
      dispatch({ type: "resetAssignIcbtState" });
      onAssignment?.();
      closeAssignIcbtModal();
    } catch {
      enqueueSnackbar({
        text: t("ASSIGN_ICBT_FAILURE"),
        isError: true,
      });
    }
  }, [
    programsToAssign,
    treatmentOrder,
    programFeedbackDates,
    treatments.byId,
    patient.userId,
    postIcbts,
    enqueueSnackbar,
    t,
    onAssignment,
    closeAssignIcbtModal,
    feedbackDateFeatureEnabledForPatient,
  ]);

  const handleOpenConfirm = () => {
    setConfirmOpen(true);
  };

  return (
    <Dialog open={true}>
      <FixedContainer>
        <AssignIcbt
          patient={patient}
          closeModal={closeModal}
          handleOpenConfirm={handleOpenConfirm}
          feedbackDateFeatureEnabledForPatient={
            feedbackDateFeatureEnabledForPatient
          }
        />
      </FixedContainer>
      <ConfirmAssignIcbtsModal
        open={confirmOpen}
        setModalIsOpen={setConfirmOpen}
        handleAssignIcbts={handleAssignIcbts}
        patientName={`${patient.firstName} ${patient.lastName}`}
        isSubmitting={isSubmitting}
      />
    </Dialog>
  );
};
